import { moduleRegistry } from './registry'
import { canAccessModule, type AuthUser } from './permissions'
import type { Permission } from './types'

/**
 * Explicit overrides for roles whose "go home" destination isn't the
 * Dashboard. Mirrors Shell.tsx's `VENDOR_LOGIN_PATHS` entry for the same
 * role (the pre-auth password-login screen already sends outside sales
 * reps to `/orders`) — keep both in sync if this role's landing page ever
 * changes. See `dashboard/module.config.ts` for why this role has no
 * Dashboard access at all.
 */
const HOME_PATH_OVERRIDES: ReadonlyArray<readonly [Permission, string]> = [
  ['outside_sales_rep', '/orders'],
]

/**
 * Resolves where a hard "go home" navigation (Connect-As, exit
 * impersonation, the sidebar brand logo) should land for this user.
 * Every such navigation must call this instead of hardcoding `/` — a
 * role can lose Dashboard access (as `outside_sales_rep` did on
 * 2026-07-21) without every hardcoded `/` site being revisited.
 */
export function getHomePath(user: AuthUser): string {
  for (const [permission, path] of HOME_PATH_OVERRIDES) {
    if (user.permissions.has(permission)) return path
  }
  const dashboard = moduleRegistry.find((m) => m.resolvedNamespace === '/')
  if (dashboard && canAccessModule(user, dashboard)) return '/'
  // Safety net for any other role that can't reach '/' and has no
  // explicit override: the first module the sidebar itself would show.
  const firstAccessible = moduleRegistry.find((m) => canAccessModule(user, m))
  return firstAccessible?.resolvedNamespace ?? '/'
}
